import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, InjectionToken, input, InputSignal, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { MenuItemCommandEvent } from 'primeng/api/menuitem';
import { BadgeModule } from 'primeng/badge';
import { Menu, MenuModule } from 'primeng/menu';
import { Ripple } from 'primeng/ripple';

import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { LanguageService } from '@core/language';
import { TranslateModule } from '@ngx-translate/core';
import { MenuButtonElementConfig, MenuButtonEntryStruct } from './types';

/**
 * A singleton token for the menu button component.
 * This allows us to keep track of the currently opened menu button.
 */
export const MENU_BUTTON_SINGLETON = new InjectionToken<WritableSignal<Menu>>('MenuButtonSingleton', {
	providedIn: 'root',
	factory: () => signal<Menu | null>(null),
});

@Component({
	selector: 'dc-menu-button',
	imports: [NgClass, MatTooltip, TranslateModule, MenuModule, Ripple, BadgeModule, NgTemplateOutlet],
	templateUrl: './menu-button.component.html',
	styleUrl: './menu-button.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class]': 'sizeClass()',
	},
})
export class MenuButtonComponent implements OnInit {
	/* --------------------------------------------------  DOM -------------------------------------------------- */

	/* ------------------------------------------  Types and Constants ------------------------------------------ */
	public readonly elementRef = inject(ElementRef);
	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	/** The list of actions to display */
	public readonly actions: InputSignal<MenuButtonEntryStruct[]> = input.required<MenuButtonEntryStruct[]>();
	public readonly data: InputSignal<unknown> = input<unknown>();
	/** The button configuration */
	public readonly buttonConfig = input<MenuButtonElementConfig, Partial<MenuButtonElementConfig>>(
		{
			icon: 'fad fa-circle-ellipsis text-primary-500 fa-fw',
			iconClass: 'transition-transform duration-300 ease-in-out',
			openClass: 'rotate-90',
		} as MenuButtonElementConfig,
		{
			transform: (value: Partial<MenuButtonElementConfig>) => {
				return {
					...value,
					icon: value?.icon || 'fad fa-circle-ellipsis text-primary-500 fa-fw',
					iconClass: value?.iconClass || 'transition-transform duration-300 ease-in-out',
					openClass: value?.openClass || 'rotate-90',
				};
			},
		},
	);
	/** The size class of the button */
	public readonly sizeClass: InputSignal<`h-${number | string} w-${number | string}`> = input('h-8 w-8');
	/** The tooltip to display */
	public tooltip: InputSignal<string> = input<string>();
	/* ------------------------------------------------  Signals ------------------------------------------------ */
	protected readonly items$: Signal<MenuItem[]> = computed<MenuItem[]>(() => {
		const actionCount = this.actions().length;
		return this.actions().reduce((acc, action, index) => {
			const menuItem = {
				label: action.label,
				icon: typeof action.iconConfig === 'string' ? action.iconConfig : action.iconConfig.icon,
				command: (event: MenuItemCommandEvent) => {
					action.action(event.originalEvent, this.data());
				},
			};
			if (index < actionCount - 1) acc.push(menuItem, {separator: true} as any);
			else acc.push(menuItem);
			return acc;
		}, [] as MenuItem[]);
	});
	protected readonly Number = Number;

	/* ------------------------------------------------  Outputs ------------------------------------------------ */
	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	private readonly languageService = inject(LanguageService);
	protected readonly tooltipPosition$ = computed<TooltipPosition>(() => {
		return this.languageService.currentLangData().oppositeLang.dir === 'rtl' ? 'left' : 'right';
	});
	protected readonly menuButtonRef = inject(MENU_BUTTON_SINGLETON);

	/* -------------------------------------------------- Data -------------------------------------------------- */

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */
	ngOnInit(): void {}

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	protected onMenuButtonClick(event: MouseEvent, menu: Menu): void {
		if (this.menuButtonRef() !== null) {
			this.menuButtonRef().hide();
		}
		this.menuButtonRef.set(menu);
		menu.toggle(event);
	}
}
